/* 메뉴 접근 판정(사이드바 노출 + 라우트 URL 가드 공용).
 *  서버 services/menu_registry.py가 정본이며, 아래 판정은 visible_menus가 없는 사용자 객체용 폴백이다.
 *  단일 원칙: 가시성은 '접근 권한' 설정에만 귀속 — 도메인·이메일 같은 코드 우회 없음.
 *  - admin: 전권(단 'admin' 메뉴는 admin만)
 *  - messenger/drive(+NC앱)/tickets: 전용 권한(messenger_access/drive_access/cs_access)
 *  - 드라이브 하위앱(office/mail/talk 등): 기본은 drive_access 전제. 단 관리자가 그 메뉴를
 *    menu_access[key]=true 로 '명시적으로' 켜면 drive_access 없이도 노출(테스트 계정 메뉴 확인용 미리보기).
 *    여전히 관리자 접근권한 설정에만 귀속(코드 우회 아님). 실데이터는 NC 계정 없으면 빈 화면일 수 있음.
 *  - board: 선개발=관리자만
 *  - dxboard: DX팀 전용=명시 허용만(기본 차단)
 *  - contracts: 시스템 관리자 + 명시 허용만(운영 역할도 명시 허용 필요 — 서버 admin_always 와 같다)
 *  - apps(AI 개발실): contracts 와 같은 판정(시스템 관리자 + 명시 허용만)
 *  - execmeeting-admin(임원회의 관리): contracts 와 같은 판정(시스템 관리자 + 명시 허용만 · 서버 admin_always)
 *  - pdas(농심DAS): contracts 와 같은 판정(시스템 관리자 + 명시 허용만)
 *  - dispatch-calc·dispatch-calc-admin(배차 계산기·기본값 관리): 운영·시스템 관리자 + 명시 허용만(서버 default_deny 와 같다)
 *  - capa(네이버 CAPA): 운영·시스템 관리자 + 명시 허용만(서버 default_deny 와 같다)
 *  - inbound-capa(입고 CAPA 모니터링): 운영·시스템 관리자 + 명시 허용만(서버 default_deny 와 같다)
 *  - claim-register·claim-standards·claim-reasons·claim-admin(클레임 등록 + 기준 보기 2 + 편집 1): 운영·시스템 관리자 + 명시 허용만(서버 default_deny 와 같다)
 *  - work-customers·work-customers-edit(고객사 기준 정보 보기 + 편집): 운영·시스템 관리자 + 명시 허용만(서버 default_deny 와 같다 · 편집 키만으로는 메뉴가 안 보인다)
 *  - center-all·center-daily·center-shippers·center-perf(센터 메뉴): 운영·시스템 관리자 + 명시 허용만(서버 default_deny 와 같다)
 *    · center:<token>(센터별): 명시 false → 숨김 · 명시 true → 노출 · 없으면 center-all 명시 허용을 따른다(서버 판정 순서와 같다)
 *  - taskpm·devreq(업무 프로젝트·개발요청): 기본 허용 · menu_access[key]===false 만 차단(서버 default_allow · 2026-11-02)
 *    관리자 토글 판정 pages/admin/shared/usePermissions.jsx menuOn 과 쌍둥이 — 한쪽만 고치면 화면과 토글이 어긋난다.
 *  - 그 외(home/help/chat/meetings/documents/inquiries/mail2/directory): menu_access[key]===false면 차단(기본 노출)
 */
// 🔴 'mail2'(메일=Roundcube)는 여기서 뺐다(마스터 지시 2026-08-24). 메일이 NC 하위앱이던 때는
//    drive_access 게이트가 맞았지만 Roundcube 는 Nextcloud 와 무관하다 — 드라이브를 안 쓰는 사람이
//    메일을 못 보는 건 근거가 없다. 이제 일반 메뉴와 같다(기본 노출, menu_access.mail2=false 로 숨김).
//    백엔드 deps._DRIVE_GATED 와 **함께** 바꿔야 한다(한쪽만 바꾸면 메뉴는 보이는데 403).
const DRIVE_GATED = ['drive', 'office', 'calendar', 'mail', 'talk', 'collabtools'];

// 운영 관리자(ops)도 관리자 화면에 들어간다(탭은 AdminPage 의 OPS_TABS 로 제한).
// 백엔드 deps.can_see_menu 와 같은 기준 — 한쪽만 고치면 메뉴가 안 보이거나 403 이 난다.
const OPS_ROLES = ['admin', 'ops'];
// 센터 메뉴(2026-10-23 일반 메뉴 권한) — 기본 차단. 센터별 키는 'center:' 접두어(계정마다 토큰이 다르다).
const CENTER_MENU_KEYS = ['center-all', 'center-daily', 'center-shippers', 'center-perf'];
const CENTER_SITE_PREFIX = 'center:';

export function canSeeMenu(user, key) {
  if (!user) return false;
  // 본인 계정 설정 — 누구에게나(서버 레지스트리도 default_allow · 비부여).
  // 배포 전 캐시된 visible_menus 에 'my' 가 없어도 입구가 사라지지 않게 배열보다 먼저 본다.
  if (key === 'my') return true;
  if (Array.isArray(user.visible_menus)) return user.visible_menus.includes(key);
  if (key === 'admin') return OPS_ROLES.includes(user.role);
  const ma = user.menu_access || {};
  if (key === 'execmeeting') return user.role === 'admin' || ma.execmeeting === true;
  if (key === 'execmeeting-admin') return user.role === 'admin' || ma['execmeeting-admin'] === true;
  if (key === 'supportboard') return user.role === 'admin' || ma.supportboard === true;
  if (key === 'contracts') return user.role === 'admin' || ma.contracts === true;
  if (key === 'apps') return user.role === 'admin' || ma.apps === true;
  if (key === 'pdas') return user.role === 'admin' || ma.pdas === true;
  if (OPS_ROLES.includes(user.role)) return true;                           // 전권
  if (key === 'work-customers' || key === 'work-customers-edit') return ma[key] === true;   // 고객사 기준 정보=명시 허용만(서버 default_deny)
  if (CENTER_MENU_KEYS.includes(key)) return ma[key] === true;              // 센터 메뉴=명시 허용만(서버 default_deny)
  if (typeof key === 'string' && key.startsWith(CENTER_SITE_PREFIX)) {      // 센터별=명시 값 → 없으면 통합조회 허용
    if (ma[key] === false) return false;
    return ma[key] === true || ma['center-all'] === true;
  }
  if (key === 'taskpm') return ma.taskpm !== false;                         // 업무 프로젝트=기본 허용 · 명시 false 만 차단
  if (key === 'devreq') return ma.devreq !== false;                         // 개발요청=기본 허용 · 명시 false 만 차단
  if (key === 'abc-analysis') return ma['abc-analysis'] === true;
  if (key === 'dispatch-calc' || key === 'dispatch-calc-admin') return ma[key] === true;   // 배차 계산기=명시 허용만
  if (key === 'capa') return ma.capa === true;                              // 네이버 CAPA=명시 허용만(서버 default_deny)
  if (key === 'inbound-capa') return ma['inbound-capa'] === true;           // 입고 CAPA 모니터링=명시 허용만(서버 default_deny)
  if (key === 'claim-register' || key === 'claim-standards' || key === 'claim-reasons' || key === 'claim-admin') return ma[key] === true;   // 클레임=명시 허용만(서버 default_deny)
  if (key === 'dxboard') return ma.dxboard === true;
  if (key === 'messenger') return !!user.messenger_access;
  // 드라이브 하위앱: 명시적 true=미리보기 노출(우회 아님, 관리자 설정), 그 외엔 drive_access 전제+앱별 carve-out
  if (DRIVE_GATED.includes(key)) return ma[key] === true || (!!user.drive_access && ma[key] !== false);
  if (key === 'tickets') return !!user.cs_access;
  if (key === 'board') return false;                                        // 선개발(비관리자 차단)
  return ma[key] !== false;                                                 // home/help/chat/meetings/documents/inquiries/directory
}
